import { useMutationObserver } from '@vueuse/core';
import { type Ref, onMounted, watchEffect } from 'vue';

import { useMissingNameWarning } from './useMissingNameWarning';

const COMBOBOX_INPUT_SELECTOR = '.ant-select-selection-search-input';

type ComboboxInputAriaType = {
  ariaLabel?: string | null;
  ariaLabelledby?: string | null;
  hasVisibleLabel?: boolean;
  id?: string | null;
  componentName: string;
  isInvalid?: boolean;
  ariaDescribedby?: string | null;
};

function setOrRemove(el: Element, attr: string, value?: string | null) {
  if (value) el.setAttribute(attr, value);
  else el.removeAttribute(attr);
}

/**
 * Puts `aria-label` / `aria-labelledby`, `aria-invalid` and `aria-describedby`
 * on the combobox input that Ant Design's Select renders, which is the element
 * that receives focus. Ant forwards these attributes to its wrapper div instead
 * (ant-design-vue 4.2.6), so a select named or marked invalid on the component
 * is still announced as an unnamed, valid combobox.
 *
 * A visible label (`hasVisibleLabel`, from the component's `formLabel`) already
 * names the input through `<label for>`, so it wins: the aria props are not
 * applied and passing both logs a warning.
 *
 * Re-applies when Ant replaces the input (for example when `showSearch` toggles)
 * and whenever the name changes. Vue does not patch attributes it did not set,
 * so ordinary re-renders keep the name.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/combobox/ (2026-10-05)
 */
export function useComboboxInputAria(
  wrapper: Ref<HTMLElement | null>,
  getAria: () => ComboboxInputAriaType,
) {
  function applyAria() {
    const input = wrapper.value?.querySelector(COMBOBOX_INPUT_SELECTOR);
    if (!input) return;
    const {
      ariaLabel,
      ariaLabelledby,
      hasVisibleLabel,
      isInvalid,
      ariaDescribedby,
    } = getAria();
    setOrRemove(input, 'aria-label', hasVisibleLabel ? null : ariaLabel);
    setOrRemove(
      input,
      'aria-labelledby',
      hasVisibleLabel ? null : ariaLabelledby,
    );
    setOrRemove(input, 'aria-invalid', isInvalid ? 'true' : null);
    setOrRemove(input, 'aria-describedby', ariaDescribedby);
  }

  onMounted(() => {
    const { ariaLabel, ariaLabelledby, hasVisibleLabel } = getAria();
    if (hasVisibleLabel && (ariaLabel || ariaLabelledby)) {
      console.warn(
        'ariaLabel/ariaLabelledby are ignored when formLabel is set; the visible label already names the select',
      );
    }
  });
  const { ariaLabel, ariaLabelledby, hasVisibleLabel, id, componentName } =
    getAria();
  useMissingNameWarning({
    isNamed: !!(hasVisibleLabel || ariaLabel || ariaLabelledby || id),
    component: componentName,
    role: 'combobox',
    hint: 'set formLabel, ariaLabel, ariaLabelledby, or id (wired to a visible <label>)',
  });
  useMutationObserver(wrapper, applyAria, { childList: true, subtree: true });
  watchEffect(applyAria, { flush: 'post' });
}
